import { AllAssortmentsModel } from '@models/all-assortments-model.interface';
import { AssortmentLineModel } from './assortment-line-model';
import { AssortmentModel } from './assortment-model';
import { ComponentModel } from './component';
import { SimpleComponentVariantModel } from './component-variant';
import { DataTranslationModel } from './data-translation-model';
import { OrderLineState } from './order-line-state';
import { VariantColorModel } from './variant-color';
import { SimpleVariantSizeModel } from './variant-size';

export class OrderLine implements AllAssortmentsModel {

	constructor(private state: OrderLineState) {
	}

	public getState = () => this.state;

	public get Id(): number {
		return this.state.Id;
	}

	public get Uuid(): string | null | undefined {
		return this.state.Uuid;
	}

	public get ComponentVariantDescription(): DataTranslationModel {
		return this.state.OriginalComponentVariant?.Description || DataTranslationModel.GetFallbackOnlyModel(this.state.ComponentVariantDescription);
	}

	public get ComponentVariantDescription2(): DataTranslationModel {
		return this.state.OriginalComponentVariant?.Description2 || DataTranslationModel.GetFallbackOnlyModel(this.state.ComponentVariantDescription2);
	}

	public get ComponentSizeDescription(): string {
		return this.state.ComponentSizeDescription;
	}

	public get ColorDescription(): DataTranslationModel {
		return this.state.OriginalComponentColor?.Color?.Description || DataTranslationModel.GetFallbackOnlyModel(this.state.ColorDescription);
	}

	public get ColorGroupDescription(): DataTranslationModel {
		return this.state.OriginalComponentColor?.Color?.ColorGroup?.Description || DataTranslationModel.GetFallbackOnlyModel(this.state.ColorGroupDescription);
	}

	public get ComponentDescription(): string {
		return this.state.ComponentDescription;
	}

	public get ComponentCode(): string {
		return this.state.ComponentCode;
	}

	public get ComponentVariantCode(): string {
		return this.state.ComponentVariantCode;
	}

	public get ComponentSizeCode(): string {
		return this.state.ComponentSizeCode;
	}

	public get ColorCode(): string | undefined {
		return this.state.ColorCode;
	}

	public get ColorGroupCode(): string | undefined {
		return this.state.ColorGroupCode;
	}

	public get ColorLineCode(): string {
		return this.state.ColorLineCode;
	}

	public get ComponentVariantUuid(): string {
		return this.state.ComponentVariantUuid;
	}

	public get ComponentSizeUuid(): string {
		return this.state.ComponentSizeUuid;
	}

	public get ComponentColorUuid(): string {
		return this.state.ComponentColorUuid;
	}

	public get ComponentUuid(): string {
		return this.state.ComponentUuid;
	}

	public get ComponentVariantBypass(): boolean {
		return this.state.ComponentVariantBypass;
	}

	public get ComponentSizeBypass(): boolean {
		return this.state.ComponentSizeBypass;
	}

	public get SplittingData(): boolean {
		return this.state.SplittingData;
	}

	public get Optional(): boolean {
		return this.state.Optional;
	}

	public get OriginalComponent(): ComponentModel {
		return this.state.OriginalComponent;
	}

	public get OriginalComponentVariant(): SimpleComponentVariantModel {
		return this.state.OriginalComponentVariant;
	}

	public get OriginalComponentSize(): SimpleVariantSizeModel {
		return this.state.OriginalComponentSize;
	}

	public get OriginalComponentColor(): VariantColorModel {
		return this.state.OriginalComponentColor;
	}

	public get AllAssortments(): Array<AssortmentModel | undefined> {
		const result = new Array<AssortmentModel | undefined>();
		this.OriginalComponentVariant?.AllAssortments.distinct((x, y) => x?.Code === y?.Code).forEach(x => result.push(x));
		this.OriginalComponentSize?.AllAssortments.distinct((x, y) => x?.Code === y?.Code).forEach(x => result.push(x));
		this.OriginalComponentColor?.AllAssortments.distinct((x, y) => x?.Code === y?.Code).forEach(x => result.push(x));

		return result;
	}

	public EngravingAvailable(): boolean {
		return this.state.EngravingPossible > 0 || this.OriginalComponentVariant?.EngravingPossible > 0;
	}

	public get AllAssortmentLines(): Array<AssortmentLineModel> {
		const result = new Array<AssortmentLineModel>();
		this.OriginalComponentVariant?.AssortmentLines.forEach(x => result.push(x));
		this.OriginalComponentSize?.AssortmentLines.forEach(x => result.push(x));
		this.OriginalComponentColor?.AssortmentLines.forEach(x => result.push(x));

		return result;
	}

	isDiscontinued(): boolean {
		const componentVariantStatus = this.isComponentVariantDiscontinued();
		const componentSizeStatus = this.isComponentSizeDiscontinued();
		const componentColorStatus = this.isComponentColorDiscontinued();

		const result = componentVariantStatus || componentSizeStatus || componentColorStatus;

		return result;
	}

	isComponentVariantDiscontinued() {
		return this.ComponentVariantCode !== null && this.ComponentVariantCode.length > 0 && (!this.OriginalComponentVariant || this.OriginalComponentVariant.isDiscontinued());
	}

	isComponentSizeDiscontinued() {
		return this.ComponentSizeCode !== null && this.ComponentSizeCode.length > 0 && (!this.OriginalComponentSize || this.OriginalComponentSize.isDiscontinued());
	}

	isComponentColorDiscontinued() {
		const componentColorStatus = !!this.ColorCode && this.ColorCode.length > 0 && (!this.OriginalComponentColor || this.OriginalComponentColor.isDiscontinued());

		return componentColorStatus;
	}

	isNew() {
		return this.isComponentVariantNew()
			|| this.isComponentSizeNew()
			|| this.isComponentColorNew();
	}

	isComponentVariantNew() {
		return this.OriginalComponentVariant && this.OriginalComponentVariant.New;
	}

	isComponentSizeNew() {
		return this.OriginalComponentSize && this.OriginalComponentSize.New;
	}

	isComponentColorNew() {
		return this.OriginalComponentColor && this.OriginalComponentColor.New;
	}

}
